import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { api } from '../../lib/api';
import { useCan } from '../../hooks/usePermission';
import { Sheet, Button, Field, Input, Select, Textarea } from '../ui';
import { PermissionMatrix } from './PermissionMatrix';
import { explicitMap, ROLE_SCOPES, type PermissionsMap, type Registry, type PermissionTemplate } from './permissions';

export type Role = { id: string; key: string; name: string; description: string | null; permissions: PermissionsMap; allAccess: boolean; scopeType: string; isSystem: boolean; isDefault?: boolean; userCount: number };


/** Create (role = null) or edit one role: its name, default data scope, full access, and the permission matrix. */
export function RoleSheet({ role, roles, registry, onClose, z = 50 }: { role: Role | null; roles: Role[]; registry: Registry; onClose: () => void; z?: number }) {
  const qc = useQueryClient();
  const isNew = !role;
  const canSave = useCan(isNew ? 'roles.create' : 'roles.update');
  const canDelete = useCan('roles.delete');
  const readOnly = !canSave;
  const [draft, setDraft] = useState(() => ({
    name: role?.name ?? '', description: role?.description ?? '', scopeType: role?.scopeType ?? 'ALL', allAccess: role?.allAccess ?? false,
    permissions: role ? (JSON.parse(JSON.stringify(role.permissions ?? {})) as PermissionsMap) : explicitMap(registry, 'none'),
  }));

  const templates = useMemo<PermissionTemplate[]>(() => roles.filter((r) => r.id !== role?.id).map((r) => ({
    id: r.id, label: `Copy from ${r.name}…`, build: () => (r.allAccess ? explicitMap(registry, 'all') : r.permissions),
  })), [roles, role, registry]);

  const done = () => { void qc.invalidateQueries({ queryKey: ['roles'] }); onClose(); };
  const save = useMutation({
    mutationFn: async () => {
      // scopeType only when it changed: the server re-checks a REGION/ENTITY/PROPERTY scope's ids on every write, and
      // seeded roles such as Agent hold one with no ids, so resending it unchanged would refuse an unrelated edit.
      const body = { name: draft.name.trim(), description: draft.description.trim() || null, permissions: draft.permissions, allAccess: draft.allAccess, ...(draft.scopeType !== role?.scopeType ? { scopeType: draft.scopeType } : {}) };
      return isNew ? api.post<Role>('/roles', body) : api.patch<Role>(`/roles/${role!.id}`, body);
    },
    onSuccess: done,
  });
  const remove = useMutation({ mutationFn: async () => api.delete(`/roles/${role!.id}`), onSuccess: done });
  const error = (save.error ?? remove.error) as Error | null;

  return (
    <Sheet open onClose={onClose} size="lg" z={z} eyebrow={isNew ? 'New role' : 'Role'} title={isNew ? 'Create a role' : role!.name}
      subtitle={!isNew && `${role!.userCount} user${role!.userCount === 1 ? '' : 's'} hold this role`}
      footer={
        <div className="w-full">
          {error && <p className="mb-3 rounded-lg border border-red-100 bg-red-50 px-3.5 py-2.5 text-[12.5px] text-red-700">{error.message}</p>}
          <div className="flex items-center justify-between gap-3">
            <div>
              {!isNew && canDelete && (
                <Button variant="ghost" size="sm" disabled={remove.isPending} onClick={() => { if (confirm(`Delete the role "${role!.name}"?${role!.userCount ? ` ${role!.userCount} user${role!.userCount === 1 ? ' holds' : 's hold'} it and must be moved to another role first.` : ''}`)) remove.mutate(); }}>
                  <Trash2 className="h-3.5 w-3.5" /> Delete role
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="secondary" onClick={onClose}>Cancel</Button>
              {!readOnly && <Button onClick={() => save.mutate()} disabled={!draft.name.trim() || save.isPending}>{save.isPending ? 'Saving…' : isNew ? 'Create role' : 'Save changes'}</Button>}
            </div>
          </div>
        </div>
      }>
      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Name"><Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="w-full" disabled={readOnly} placeholder="e.g. Property manager" /></Field>
          <Field label="Default data scope" hint="Which records holders see. Region, company and property scopes are set on each user, where you pick which ones.">
            <Select value={draft.scopeType} onChange={(e) => setDraft({ ...draft, scopeType: e.target.value })} className="w-full" disabled={readOnly}>
              {ROLE_SCOPES.filter(([k]) => k === 'ALL' || k === 'OWN' || k === role?.scopeType).map(([k, desc]) => <option key={k} value={k}>{k.charAt(0) + k.slice(1).toLowerCase()} — {desc.toLowerCase()}</option>)}
            </Select>
          </Field>
          <div className="sm:col-span-2"><Field label="Description"><Textarea rows={2} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} className="w-full" disabled={readOnly} /></Field></div>
          <label className={`flex items-start gap-3 rounded-lg border p-3 text-[13px] sm:col-span-2 ${draft.allAccess ? 'border-bronze-500 bg-bronze-50' : 'border-ink-200'}`}>
            <input type="checkbox" checked={draft.allAccess} disabled={readOnly} onChange={(e) => setDraft({ ...draft, allAccess: e.target.checked })} className="mt-0.5 h-4 w-4 accent-bronze-500" />
            <span><span className="font-medium text-ink-900">Full access</span><span className="block text-[11.5px] text-ink-500">Grants every action on every page. The platform refuses to remove this from the last role that has it — otherwise nobody could administer it.</span></span>
          </label>
        </div>

        <div>
          <div className="mb-1.5 text-[12px] font-medium text-ink-600">Page permissions</div>
          {draft.allAccess
            ? <div className="rounded-lg border border-ink-200 p-4 text-[12.5px] text-ink-500">This role has full access; individual permissions are ignored.</div>
            : <PermissionMatrix registry={registry} value={draft.permissions} onChange={(permissions) => setDraft((d) => ({ ...d, permissions }))} disabled={readOnly} templates={templates} />}
        </div>
      </div>
    </Sheet>
  );
}
